<script setup lang="ts">
const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;

definePageMeta({
  layout: "static",
});

useSeoMeta({
  title: "Privacy Policy | &Dine",
  description:
    "Read the &Dine privacy policy. Learn how we collect, process, and protect your personal data in compliance with UK GDPR.",
});

useHead({
  link: [{ rel: "canonical", href: `${siteOrigin}/privacy-policy` }],
});
</script>

<template>
  <section class="privacy-policy">
    <h1 class="grey-text">Privacy Policy</h1>
    <p class="grey-text"><em>Last updated: January 2026</em></p>

    <!--
      Section numbering is generated by CSS counters on .privacy-policy ol — do not
      hard-code numbers into the headings or they will render twice.
    -->
    <ol>
      <li class="grey-text">
        <strong>Introduction</strong>
        <p class="grey-text">
          This Privacy Policy explains how &amp;Dine Limited collects, uses, shares, and protects
          personal data when you interact with our website, platform, and services.
        </p>
        <p class="grey-text">
          &amp;Dine operates a two-sided marketplace connecting businesses, individuals, and
          independent food Makers. Depending on how you use &amp;Dine, we may act as a data
          controller, data processor, or joint controller under UK data protection law.
        </p>
        <p class="grey-text">
          This Privacy Policy should be read alongside any additional notices provided at the point
          we collect personal data.
        </p>
      </li>

      <li class="grey-text">
        <strong>Who we are</strong>
        <p class="grey-text">&amp;Dine Limited</p>
        <p class="grey-text">
          Registered address: Beyond, Fox Court, 14 Gray&rsquo;s Inn Road, London, WC1X 8HN
        </p>
        <p class="grey-text">
          Email:
          <a href="mailto:hello@anddine.co.uk">hello@anddine.co.uk</a>
        </p>
        <p class="grey-text">
          For the purposes of UK data protection law, &amp;Dine Limited is responsible for ensuring
          personal data is processed lawfully.
        </p>
      </li>

      <li class="grey-text">
        <strong>Our role in relation to your data</strong>

        <p class="grey-text"><strong>Corporate customers</strong></p>
        <p class="grey-text">
          When a company creates an organisation account on &amp;Dine, that company acts as the data
          controller for the personal data of its employees uploaded to or managed within the
          account.
        </p>
        <p class="grey-text">
          This typically includes names, work email addresses, telephone numbers, and role or
          permission information.
        </p>
        <p class="grey-text">
          In these circumstances, &amp;Dine acts as a data processor and processes personal data
          only on the instructions of the company and for the purpose of providing the &amp;Dine
          service.
        </p>
        <p class="grey-text">
          Where a company asks &amp;Dine to assist with onboarding by uploading employee details on
          its behalf, the company remains responsible for ensuring it has a lawful basis to share
          that data with us.
        </p>

        <p class="grey-text"><strong>Employee users within a company account</strong></p>
        <p class="grey-text">
          Employees invited to use &amp;Dine through their employer generally use the platform as
          part of their employer&rsquo;s account.
        </p>
        <p class="grey-text">For account administration and ordering:</p>
        <ul class="grey-text">
          <li>the employer acts as data controller</li>
          <li>&amp;Dine acts as data processor</li>
        </ul>
        <p class="grey-text">
          Where an employee manages their own profile, selects dietary preferences or allergens,
          interacts directly with Makers, or opts into communications from &amp;Dine, &amp;Dine may
          act as a joint controller or independent controller for those specific activities.
        </p>

        <p class="grey-text"><strong>Individual and guest users</strong></p>
        <p class="grey-text">
          Individuals may use &amp;Dine outside of a company account, including guest users and
          individuals ordering food for personal use or delivery to residential addresses.
        </p>
        <p class="grey-text">
          In these cases, &amp;Dine acts as the data controller for personal data processed in
          connection with account creation, ordering, payment, delivery coordination, and support.
        </p>
        <p class="grey-text">
          All users, including guest users, are subject to &amp;Dine&rsquo;s Terms of Service and
          this Privacy Policy.
        </p>

        <p class="grey-text"><strong>Makers and suppliers</strong></p>
        <p class="grey-text">
          &amp;Dine acts as a data controller for personal data provided by or about Makers and
          their authorised users. This may include business contact details, login credentials, and
          communications.
        </p>
        <p class="grey-text">
          Where Makers ask &amp;Dine to assist with onboarding using information they provide, we
          process that data for the purpose of operating the marketplace and based on their
          instructions.
        </p>
      </li>

      <li class="grey-text">
        <strong>Food preferences and special category data</strong>
        <p class="grey-text">Users may optionally provide information relating to:</p>
        <ul class="grey-text">
          <li>allergens</li>
          <li>dietary preferences</li>
          <li>religious dietary requirements</li>
          <li>intolerance-related information</li>
        </ul>
        <p class="grey-text">
          This information is used solely to fulfil food orders safely and accurately. It may be
          visible to Makers, employers, and relevant guests where necessary to deliver the service.
        </p>
        <p class="grey-text">
          Where this information qualifies as special category data, we process it on the basis of
          explicit consent or because it is necessary to provide the service requested.
        </p>
        <p class="grey-text">Users can update or remove this information at any time.</p>
      </li>

      <li class="grey-text">
        <strong>What personal data we collect</strong>
        <p class="grey-text">Depending on how you interact with &amp;Dine, we may collect:</p>

        <p class="grey-text"><strong>Identity and contact data</strong></p>
        <ul class="grey-text">
          <li>Name</li>
          <li>Work or personal email address</li>
          <li>Telephone number</li>
          <li>Job title or role where relevant</li>
        </ul>

        <p class="grey-text"><strong>Account and usage data</strong></p>
        <ul class="grey-text">
          <li>Login credentials</li>
          <li>Role and permission level</li>
          <li>Order history and preferences</li>
          <li>Communications with us</li>
        </ul>

        <p class="grey-text"><strong>Food-related data</strong></p>
        <ul class="grey-text">
          <li>Dietary preferences</li>
          <li>Allergens</li>
          <li>Other food-related requirements provided voluntarily</li>
        </ul>

        <p class="grey-text"><strong>Transactional and billing data</strong></p>
        <ul class="grey-text">
          <li>Order values</li>
          <li>Payment status</li>
          <li>Invoice references</li>
          <li>Billing and delivery addresses</li>
        </ul>

        <p class="grey-text"><strong>Delivery data</strong></p>
        <ul class="grey-text">
          <li>Delivery instructions</li>
          <li>Proof of delivery records (including photographs)</li>
        </ul>

        <p class="grey-text"><strong>Technical data</strong></p>
        <ul class="grey-text">
          <li>IP address</li>
          <li>Browser type and version</li>
          <li>Device and operating system information</li>
          <li>Usage and interaction data</li>
        </ul>

        <p class="grey-text">We do not knowingly collect data about children.</p>
      </li>

      <li class="grey-text">
        <strong>How we collect personal data</strong>
        <p class="grey-text">We collect personal data:</p>
        <ul class="grey-text">
          <li>
            directly from you when you create an account, place an order, or update your profile
          </li>
          <li>from your employer when they create or manage an organisation account</li>
          <li>from Makers during onboarding and ongoing account use</li>
          <li>automatically through use of our website and platform</li>
          <li>
            through third-party tools used to operate our business, such as CRM and support systems
          </li>
        </ul>
        <p class="grey-text">
          Where personal data is provided by a third party, they are responsible for ensuring they
          have a lawful basis to do so.
        </p>
      </li>

      <li class="grey-text">
        <strong>How we use personal data</strong>
        <p class="grey-text">We use personal data where permitted by law, including where:</p>
        <ul class="grey-text">
          <li>it is necessary to perform a contract</li>
          <li>it is necessary for our legitimate business interests</li>
          <li>we are required to comply with a legal obligation</li>
          <li>you have given consent</li>
        </ul>
        <p class="grey-text">We use personal data to:</p>
        <ul class="grey-text">
          <li>operate and manage the &amp;Dine platform</li>
          <li>administer accounts, permissions, and access</li>
          <li>process and fulfil food orders</li>
          <li>coordinate and confirm deliveries</li>
          <li>communicate with users, customers, Makers, and delivery partners</li>
          <li>manage billing, invoicing, and payments</li>
          <li>improve products and services</li>
          <li>ensure platform security and prevent fraud</li>
          <li>meet legal, tax, and regulatory obligations</li>
        </ul>
        <p class="grey-text">
          Where personal data is shared with delivery partners, processing is necessary for the
          performance of a contract and for our legitimate interest in fulfilling orders, preventing
          fraud, and maintaining service quality.
        </p>
      </li>

      <li class="grey-text">
        <strong>Payments</strong>
        <p class="grey-text">&amp;Dine does not store or process card details.</p>
        <p class="grey-text">
          Payments are handled by third-party payment providers such as Stripe or via bank transfer.
          These providers process payment information independently and securely.
        </p>
        <p class="grey-text">
          &amp;Dine receives only transactional data such as payment status, amounts, and references
          necessary for accounting, reconciliation, and support.
        </p>
        <p class="grey-text">
          Billing and delivery addresses may be stored at account or company level where required to
          fulfil orders and manage invoicing.
        </p>
      </li>

      <li class="grey-text">
        <strong>Marketing and communications</strong>
        <p class="grey-text">We may send communications about &amp;Dine where permitted by law.</p>
        <p class="grey-text">
          Where we communicate with users at work, this is generally treated as communication with
          the organisation. You can opt out at any time.
        </p>
        <p class="grey-text">
          Where consent is required, we will only send marketing communications with your consent.
          You can withdraw consent or opt out at any time.
        </p>
        <p class="grey-text">
          We do not sell personal data to third parties for marketing purposes.
        </p>
      </li>

      <li class="grey-text">
        <strong>Delivery partners and proof of delivery</strong>
        <p class="grey-text">
          To fulfil food orders, &amp;Dine shares limited personal data with third-party delivery
          partners via secure integrations and APIs. This typically includes the delivery address,
          contact name, telephone number, and any delivery instructions required to complete the
          drop-off.
        </p>
        <p class="grey-text">
          Delivery drivers may access this information only for the purpose of completing the
          delivery and only for the duration necessary to do so.
        </p>
        <p class="grey-text">
          As part of delivery confirmation, drivers may upload proof of delivery, which can include
          photographs of the food being handed over or left at a delivery location. These images may
          incidentally include individuals or personal surroundings. Proof of delivery is used
          solely to confirm fulfilment of an order, resolve disputes, and maintain service quality.
        </p>
        <p class="grey-text">
          Depending on the delivery partner and service model, delivery partners may act as data
          processors on behalf of &amp;Dine or as independent data controllers. Where required,
          appropriate contractual and data protection safeguards are in place.
        </p>
        <p class="grey-text">
          Delivery data is not used for marketing purposes and is retained only in line with
          operational and legal requirements.
        </p>
      </li>

      <li class="grey-text">
        <strong>Data sharing</strong>
        <p class="grey-text">
          We may share personal data with trusted third parties where necessary to operate our
          services, including:
        </p>
        <ul class="grey-text">
          <li>payment providers</li>
          <li>hosting and infrastructure providers (such as AWS)</li>
          <li>CRM and customer support platforms (such as HubSpot)</li>
          <li>delivery partners</li>
          <li>professional advisers</li>
        </ul>
        <p class="grey-text">
          All third parties are required to process data securely and only in accordance with
          applicable data protection laws and contractual safeguards.
        </p>
        <p class="grey-text">
          We may also disclose data where required by law or in connection with a business sale,
          investment, or restructuring.
        </p>
      </li>

      <li class="grey-text">
        <strong>International data processing</strong>
        <p class="grey-text">
          &amp;Dine uses infrastructure and service providers based in the UK, EU, and US.
        </p>
        <p class="grey-text">
          Members of our technology team and contractors may access systems from outside the UK,
          including Serbia. All access is limited, monitored, and subject to confidentiality and
          contractual safeguards.
        </p>
        <p class="grey-text">
          Where personal data is transferred outside the UK or EEA, we ensure appropriate safeguards
          are in place, including standard contractual clauses or equivalent protections.
        </p>
      </li>

      <li class="grey-text">
        <strong>Data security</strong>
        <p class="grey-text">
          We implement appropriate technical and organisational measures to protect personal data,
          including access controls, secure infrastructure, and confidentiality obligations.
        </p>
        <p class="grey-text">
          You are responsible for keeping your account credentials confidential. We will never ask
          you for your password.
        </p>
      </li>

      <li class="grey-text">
        <strong>Data retention</strong>
        <p class="grey-text">We retain personal data only for as long as necessary to:</p>
        <ul class="grey-text">
          <li>provide our services</li>
          <li>meet legal, accounting, and tax requirements</li>
          <li>resolve disputes</li>
          <li>enforce agreements</li>
        </ul>
        <p class="grey-text">
          When data is no longer required, it is securely deleted or anonymised.
        </p>
      </li>

      <li class="grey-text">
        <strong>Your rights</strong>
        <p class="grey-text">
          You have rights under UK data protection law, including the right to:
        </p>
        <ul class="grey-text">
          <li>access your personal data</li>
          <li>correct inaccurate or incomplete data</li>
          <li>request deletion</li>
          <li>restrict or object to processing</li>
          <li>request data portability</li>
          <li>withdraw consent where applicable</li>
        </ul>
        <p class="grey-text">
          Where &amp;Dine acts as a data processor on behalf of a company, requests should usually
          be directed to that company.
        </p>
      </li>

      <li class="grey-text">
        <strong>Cookies</strong>
        <p class="grey-text">
          <!--
            The supplied January 2026 policy refers to a Cookie Policy. No such page
            exists on the site yet, so this is deliberately NOT a link — a NuxtLink to
            /cookie-policy would 404. Turn it into a link as soon as that page ships.
          -->
          We use cookies and similar technologies to operate and improve our website and platform.
          Details are set out in our Cookie Policy.
        </p>
      </li>

      <li class="grey-text">
        <strong>Changes to this policy</strong>
        <p class="grey-text">
          We may update this Privacy Policy from time to time. The most recent version will always
          be available on our website and clearly dated.
        </p>
      </li>

      <li class="grey-text">
        <strong>Contact</strong>
        <p class="grey-text">
          If you have any questions about this Privacy Policy or how we handle personal data,
          contact us at:
        </p>
        <p class="grey-text">&amp;Dine Limited</p>
        <p class="grey-text">Beyond, Fox Court, 14 Gray&rsquo;s Inn Road, London, WC1X 8HN</p>
        <p class="grey-text">
          Email:
          <a href="mailto:hello@anddine.co.uk">hello@anddine.co.uk</a>
        </p>
        <p class="grey-text">
          You also have the right to complain to the Information Commissioner&rsquo;s Office.
        </p>
      </li>
    </ol>
  </section>
</template>

<style lang="scss">
.privacy-policy {
  width: 90%;
  padding: 50px 5%;
  background-color: var(--color-white-1);
  margin-top: 80px;
  @media (max-width: $tablet) {
    margin-top: 70px;
  }
  p {
    color: var(--color-grey-1);
  }
  h1 {
    color: var(--color-grey-1);
  }
  ol {
    list-style-type: none;
    counter-reset: item;
    margin: 20px 0;
    padding: 0;
  }
  ol > li {
    display: table;
    counter-increment: item;
    margin-bottom: 10px;
  }
  ol > li:before {
    content: counters(item, ".") ". ";
    display: table-cell;
    padding-right: 10px;
  }
  li ol > li {
    margin: 0;
  }
  li ol > li:before {
    content: counters(item, ".") " ";
  }
  // Bullet lists inside a numbered section must not inherit the counter styling.
  ul {
    list-style-type: disc;
    margin: 10px 0;
    padding-left: 20px;
    color: var(--color-grey-1);
    li {
      display: list-item;
      margin-bottom: 4px;
    }
  }
  table {
    width: 100%;
    border-collapse: collapse;
    th,
    tr,
    td {
      border: 2px solid var(--color-grey-3);
      padding: 1%;
    }
  }
  strong {
    line-height: 40px !important;
  }
}
</style>
